class KlipCouponPrice {
  constructor(section) {
    this.section = section;
    this.discountType = null;
    this.percentage = null;
    this.fixedAmountCents = null;
    this.renderState = null;
    this.updateQueued = false;
    this.couponSelector = '.elsklip-coupon-box, .elsklip-coupon__container';
    this.percentagePattern = /(\d+(?:[.,]\d+)?)\s*%/;
    this.fixedAmountPattern = /(?:€|EUR)\s*(\d+(?:[.,]\d{1,2})?)|(\d+(?:[.,]\d{1,2})?)\s*(?:€|EUR)/i;
    this.priceSelector = '[id^="price-"] .price[data-price-cents]';

    this.onSectionChange = this.onSectionChange.bind(this);
    this.section.addEventListener('change', this.onSectionChange);

    this.observer = new MutationObserver(() => {
      this.queueUpdate();
    });

    this.observer.observe(this.section, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.queueUpdate();
  }

  onSectionChange() {
    this.queueUpdate();
  }

  queueUpdate() {
    if (this.updateQueued) {
      return;
    }

    this.updateQueued = true;

    requestAnimationFrame(() => {
      this.updateQueued = false;
      this.update();
    });
  }

  update() {
    const couponBox = this.section.querySelector(this.couponSelector);
    const priceElement = this.section.querySelector(this.priceSelector);

    if (!couponBox) {
      this.clearDiscount();
      this.removeCouponPrice(priceElement);
      return;
    }

    this.rememberDiscount(couponBox);

    const checkbox = couponBox.querySelector('input[type="checkbox"]');
    const isChecked = checkbox ? checkbox.checked : false;
    const hasDiscount = this.discountType === 'percentage' || this.discountType === 'fixed';

    if (!isChecked || !hasDiscount || !priceElement) {
      this.removeCouponPrice(priceElement);
      return;
    }

    const originalPrice = parseInt(priceElement.getAttribute('data-price-cents'), 10);

    if (Number.isNaN(originalPrice)) {
      this.removeCouponPrice(priceElement);
      return;
    }

    const discountedPrice = this.calculateDiscountedPrice(originalPrice);

    if (discountedPrice === null) {
      this.removeCouponPrice(priceElement);
      return;
    }

    const crossedOutPrice = this.getCrossedOutPrice(priceElement, originalPrice);

    this.renderCouponPrice(priceElement, crossedOutPrice, discountedPrice);
  }

  getCrossedOutPrice(priceElement, activePrice) {
    const compareAtPrice = parseInt(priceElement.getAttribute('data-compare-at-cents'), 10);

    if (!Number.isNaN(compareAtPrice) && compareAtPrice > activePrice) {
      return compareAtPrice;
    }

    return activePrice;
  }

  clearDiscount() {
    this.discountType = null;
    this.percentage = null;
    this.fixedAmountCents = null;
  }

  rememberDiscount(couponBox) {
    const couponText = couponBox.textContent || '';
    const percentageMatch = couponText.match(this.percentagePattern);

    if (percentageMatch) {
      const percentageText = percentageMatch[1].replace(',', '.');
      const percentage = parseFloat(percentageText);

      if (Number.isNaN(percentage)) {
        return;
      }

      this.discountType = 'percentage';
      this.percentage = percentage;
      this.fixedAmountCents = null;
      return;
    }

    const fixedAmountCents = this.readFixedAmountCents(couponText);

    if (fixedAmountCents === null) {
      return;
    }

    this.discountType = 'fixed';
    this.fixedAmountCents = fixedAmountCents;
    this.percentage = null;
  }

  readFixedAmountCents(couponText) {
    const match = couponText.match(this.fixedAmountPattern);

    if (!match) {
      return null;
    }

    const amountText = match[1] || match[2];
    let normalizedAmount = amountText;

    if (amountText.indexOf(',') !== -1) {
      normalizedAmount = amountText.replace(/\./g, '').replace(',', '.');
    }

    const amount = parseFloat(normalizedAmount);

    if (Number.isNaN(amount)) {
      return null;
    }

    return Math.round(amount * 100);
  }

  calculateDiscountedPrice(originalPrice) {
    if (this.discountType === 'percentage' && this.percentage !== null) {
      const discountAmount = Math.floor((originalPrice * this.percentage) / 100);
      return originalPrice - discountAmount;
    }

    if (this.discountType === 'fixed' && this.fixedAmountCents !== null) {
      const discountedPrice = originalPrice - this.fixedAmountCents;

      if (discountedPrice < 0) {
        return 0;
      }

      return discountedPrice;
    }

    return null;
  }

  renderCouponPrice(priceElement, crossedOutPrice, discountedPrice) {
    const discountLabel = this.discountType === 'fixed' ? 'fixed:' + this.fixedAmountCents : 'percentage:' + this.percentage;
    const nextState = crossedOutPrice + '|' + discountedPrice + '|' + discountLabel;
    const existingPrice = priceElement.querySelector('.klip-coupon-price');

    if (this.renderState === nextState && existingPrice) {
      priceElement.classList.add('price--klip-coupon');
      const moneyFormat = this.section.getAttribute('data-money-format') || '{{amount}}';
      this.updateSaleBadge(priceElement, crossedOutPrice, discountedPrice, moneyFormat);
      return;
    }

    this.renderState = nextState;
    this.removeCustomPrice(priceElement);

    const moneyFormat = this.section.getAttribute('data-money-format') || '{{amount}}';
    const originalLabel = this.formatMoney(crossedOutPrice, moneyFormat);
    const discountedLabel = this.formatMoney(discountedPrice, moneyFormat);

    const wrapper = document.createElement('div');
    wrapper.className = 'klip-coupon-price';

    const originalElement = document.createElement('s');
    originalElement.className = 'price-item klip-coupon-price__original';
    originalElement.textContent = originalLabel;

    const discountedElement = document.createElement('span');
    discountedElement.className = 'price-item klip-coupon-price__discounted';
    discountedElement.textContent = discountedLabel;

    wrapper.appendChild(originalElement);
    wrapper.appendChild(discountedElement);

    const existingBadge = priceElement.querySelector('.price__badge-sale');

    if (existingBadge) {
      priceElement.insertBefore(wrapper, existingBadge);
    } else {
      priceElement.appendChild(wrapper);
    }

    priceElement.classList.add('price--klip-coupon');
    this.updateSaleBadge(priceElement, crossedOutPrice, discountedPrice, moneyFormat);
  }

  removeCouponPrice(priceElement) {
    this.renderState = null;

    if (!priceElement) {
      return;
    }

    this.removeCustomPrice(priceElement);
    this.restoreSaleBadge(priceElement);
    priceElement.classList.remove('price--klip-coupon');
  }

  updateSaleBadge(priceElement, crossedOutPrice, discountedPrice, moneyFormat) {
    const badge = this.findOrCreateSaleBadge(priceElement);
    const badgeText = this.getBadgeText(priceElement, crossedOutPrice, discountedPrice, moneyFormat);

    if (badge.textContent !== badgeText) {
      badge.textContent = badgeText;
    }
  }

  findOrCreateSaleBadge(priceElement) {
    const existingBadge = priceElement.querySelector('.price__badge-sale');

    if (existingBadge) {
      if (!existingBadge.dataset.klipOriginalText) {
        existingBadge.dataset.klipOriginalText = existingBadge.textContent;
      }

      return existingBadge;
    }

    const badge = document.createElement('span');
    const schemeClass = priceElement.getAttribute('data-sale-badge-scheme') || '';
    badge.className = 'badge price__badge-sale ' + schemeClass;
    badge.dataset.klipCreated = 'true';
    priceElement.appendChild(badge);

    return badge;
  }

  restoreSaleBadge(priceElement) {
    const badge = priceElement.querySelector('.price__badge-sale');

    if (!badge) {
      return;
    }

    if (badge.dataset.klipCreated === 'true') {
      badge.remove();
      return;
    }

    if (badge.dataset.klipOriginalText) {
      badge.textContent = badge.dataset.klipOriginalText;
      delete badge.dataset.klipOriginalText;
    }
  }

  getBadgeText(priceElement, crossedOutPrice, discountedPrice, moneyFormat) {
    const discountAmount = crossedOutPrice - discountedPrice;
    const badgeFormat = priceElement.getAttribute('data-badge-format') || 'percent';

    if (badgeFormat === 'euro') {
      return 'Spare ' + this.formatMoney(discountAmount, moneyFormat);
    }

    let percent = 0;

    if (crossedOutPrice > 0) {
      percent = Math.round((discountAmount * 100) / crossedOutPrice);
    }

    let percentLabel = String(percent);

    if (percentLabel.indexOf('.') !== -1) {
      percentLabel = percentLabel.replace('.', ',');
    }

    return percentLabel + '% Rabatt';
  }

  removeCustomPrice(priceElement) {
    const customPrice = priceElement.querySelector('.klip-coupon-price');

    if (customPrice) {
      customPrice.remove();
    }
  }

  formatMoney(cents, format) {
    const placeholderPattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderPattern);

    if (!match) {
      return format;
    }

    let formattedAmount = '';

    if (match[1] === 'amount') {
      formattedAmount = this.formatAmount(cents, 2, ',', '.');
    } else if (match[1] === 'amount_no_decimals') {
      formattedAmount = this.formatAmount(cents, 0, ',', '.');
    } else if (match[1] === 'amount_with_comma_separator') {
      formattedAmount = this.formatAmount(cents, 2, '.', ',');
    } else if (match[1] === 'amount_no_decimals_with_comma_separator') {
      formattedAmount = this.formatAmount(cents, 0, '.', ',');
    } else if (match[1] === 'amount_with_apostrophe_separator') {
      formattedAmount = this.formatAmount(cents, 2, "'", '.');
    } else {
      formattedAmount = this.formatAmount(cents, 2, ',', '.');
    }

    return format.replace(placeholderPattern, formattedAmount);
  }

  formatAmount(cents, precision, thousandsSeparator, decimalSeparator) {
    const fixedAmount = (cents / 100).toFixed(precision);
    const amountParts = fixedAmount.split('.');
    const wholeAmount = amountParts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousandsSeparator);
    const decimalAmount = amountParts[1] ? decimalSeparator + amountParts[1] : '';

    return wholeAmount + decimalAmount;
  }
}

function initKlipCouponPrice() {
  const sections = document.querySelectorAll('[data-klip-coupon-price]');

  sections.forEach((section) => {
    if (section.dataset.klipCouponPriceReady === 'true') {
      return;
    }

    section.dataset.klipCouponPriceReady = 'true';
    new KlipCouponPrice(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKlipCouponPrice);
} else {
  initKlipCouponPrice();
}

document.addEventListener('shopify:section:load', initKlipCouponPrice);

